/**
 * EditorAssetLibrary: the catalog of placeable assets for the Scene Builder.
 *
 * Assets are declared in `/assets/editor/asset-library.json` (a bare JSON array).
 * Each entry maps to a `SceneObject` factory: a `marker` (visual), an
 * `objectType`, and optional default interaction/portal hints. The editor
 * toolbar renders one button per asset (grouped by `category`); placing one
 * calls `createObjectFromAsset` to mint a fresh `SceneObject`.
 *
 * If the fetch fails the in-memory `DEFAULT_ASSETS` fallback is used so the
 * editor always has something to place.
 */

import type {
    SceneObject,
    SceneObjectType,
    SceneEventAction,
    InteractionMode,
    MarkerConfig,
    VideoScreenConfig,
    SceneObjectColliderConfig
} from '../scene/SceneObjectTypes';

export interface EditorAsset {
    id: string;
    name: string;
    /** Short one-line description shown under the name in the config panel. */
    description?: string;
    category: string;
    objectType: SceneObjectType;
    marker: MarkerConfig;
    defaultInteraction?: { mode: InteractionMode; radius?: number; size?: [number, number, number] };
    /** Video-screen defaults filled on placement. */
    video?: VideoScreenConfig;
    /** Collision-only or collision-enabled object defaults filled on placement. */
    collider?: SceneObjectColliderConfig;
    /** Portal assets fill in a `portal` config on placement. */
    isPortal?: boolean;
    thumbnail?: string;
}

const URL = '/assets/editor/asset-library.json';

const DEFAULT_ASSETS: EditorAsset[] = [
    {
        id: 'hotspot',
        name: '信息热点',
        description: '球形标记，不同交互模式显示信息面板',
        category: '触发点',
        objectType: 'hotspot',
        marker: { kind: 'primitive', shape: 'sphere', color: [0.4, 1.0, 0.5], scale: [0.8, 0.8, 0.8], opacity: 0.7 },
        // 默认走入触发：pressE 需要键盘 E（手机端无键）且历史上被永远为假的
        // 指针锁定闸门拦死（见 main.ts KeyE 注释）；enter 与传送门同路径，
        // 桌面/手机都能用。设计者仍可在属性面板按对象改回 pressE。
        defaultInteraction: { mode: 'enter', radius: 4 }
    },
    {
        id: 'portal',
        name: '传送门',
        description: '走入即切换到目标场景',
        category: '传送门',
        objectType: 'portal',
        isPortal: true,
        marker: { kind: 'primitive', shape: 'box', color: [1.0, 0.85, 0.3], scale: [5, 5, 5], opacity: 0.5 },
        defaultInteraction: { mode: 'enter', radius: 3.5 }
    },
    {
        id: 'triggerZone',
        name: '触发区域',
        description: '进入盒体范围自动触发事件',
        category: '区域',
        objectType: 'triggerZone',
        marker: { kind: 'primitive', shape: 'box', color: [0.6, 0.7, 1.0], scale: [6, 6, 6], opacity: 0.25 },
        defaultInteraction: { mode: 'enter', size: [6, 6, 6] }
    },
    {
        id: 'collider',
        name: '碰撞体',
        description: '放置一个可阻挡玩家的碰撞体，支持盒子、球形、圆柱、胶囊',
        category: '碰撞',
        objectType: 'collider',
        marker: { kind: 'primitive', shape: 'box', color: [1.0, 0.35, 0.25], scale: [1, 1, 1], opacity: 0.22 },
        collider: { shape: 'box', center: [0, 0, 0], size: [1, 1, 1] }
    },
    {
        id: 'videoScreen',
        name: '视频屏幕',
        description: '在场景内播放视频的面片/GLB 屏幕',
        category: '媒体',
        objectType: 'videoScreen',
        marker: { kind: 'video', scale: [4, 2.25, 1] },
        video: { url: '', autoplay: false, defaultVisible: true, loop: true, muted: true, volume: 1, emissiveIntensity: 1.8 }
    }
    // 信息标签 (label) removed: it had no runtime behavior beyond a static
    // marker — 信息热点 covers labeled info points and is interactive.
];

function markerScaleVec(marker: MarkerConfig): [number, number, number] {
    if (marker.kind === 'primitive' || marker.kind === 'video' || marker.kind === 'imagePanel') {
        return marker.scale ?? [1, 1, 1];
    }
    const s = marker.scale ?? 1;
    return [s, s, s];
}

export class EditorAssetLibrary {
    private _assets: EditorAsset[] = [];
    private _byId = new Map<string, EditorAsset>();
    loaded = false;

    async load(): Promise<void> {
        try {
            const res = await fetch(URL, { cache: 'no-store' });
            if (res.ok) this._assets = (await res.json()) as EditorAsset[];
        } catch {
            /* fall back to defaults below */
        }
        if (!this._assets.length) this._assets = DEFAULT_ASSETS;
        this._byId.clear();
        for (const a of this._assets) this._byId.set(a.id, a);
        this.loaded = true;
    }

    list(): EditorAsset[] { return this._assets; }
    get(id: string): EditorAsset | undefined { return this._byId.get(id); }
}

/** Context for minting a placed object. */
export interface PlaceCtx {
    sceneId: string;
    scenes: string[];
}

let _counter = 0;
function nextId(prefix: string, existing?: Set<string>): string {
    let id: string;
    do {
        id = `${prefix}-${++_counter}`;
    } while (existing && existing.has(id));
    return id;
}

/** Mint a unique object id for an editor-created object (placement / duplicate).
 *  Pass the set of already-occupied ids to avoid collisions with loaded objects. */
export function mintObjectId(prefix: string, existing?: Set<string>): string {
    return nextId(prefix, existing);
}

function defaultEvents(asset: EditorAsset): SceneEventAction[] {
    if (asset.objectType === 'portal' || asset.objectType === 'model' || asset.objectType === 'collider') return [];
    return [
        {
            id: nextId('evt'),
            type: 'showPanel',
            name: '显示信息',
            payload: { title: asset.name, content: `${asset.name}（编辑器创建）` }
        }
    ];
}

/** Mint a fresh SceneObject from an asset at a world position.
 *  Pass existingIds to avoid id collisions with already-loaded objects. */
export function createObjectFromAsset(asset: EditorAsset, pos: [number, number, number], ctx: PlaceCtx, existingIds?: Set<string>): SceneObject {
    // The marker entity bakes `marker.scale` into its localScale, so initialize
    // `transform.scale` to match — this keeps the gizmo, property panel, and
    // marker all agreeing on scale from the moment the object is placed.
    // Deep-clone the marker and scale so every placed object owns an
    // independent copy — the asset singleton is shared by all instances, and
    // writing to marker.scale / transform.scale must not leak across objects.
    const baseScale = [...markerScaleVec(asset.marker)] as [number, number, number];

    const obj: SceneObject = {
        id: nextId(asset.objectType, existingIds),
        type: asset.objectType,
        name: asset.name,
        transform: { position: [pos[0], pos[1], pos[2]], rotation: [0, 0, 0], scale: baseScale },
        marker: JSON.parse(JSON.stringify(asset.marker)) as MarkerConfig
    };

    if (asset.defaultInteraction) {
        obj.interaction = {
            mode: asset.defaultInteraction.mode,
            radius: asset.defaultInteraction.radius,
            size: asset.defaultInteraction.size,
            events: defaultEvents(asset)
        };
    }

    if (asset.collider) {
        obj.collider = { ...asset.collider };
        if (asset.collider.center) obj.collider.center = [...asset.collider.center] as [number, number, number];
        if (asset.collider.size) obj.collider.size = [...asset.collider.size] as [number, number, number];
    }

    if (asset.objectType === 'portal') {
        const target = ctx.scenes.find((s) => s !== ctx.sceneId) ?? ctx.scenes[0] ?? '';
        const color = asset.marker.kind === 'primitive' ? asset.marker.color : undefined;
        obj.portal = {
            targetScene: target,
            targetSpawn: [0, 5, 0],
            targetAngles: [-12, 0, 0],
            color
        };
    }

    if (asset.objectType === 'triggerZone') {
        obj.trigger = { size: asset.defaultInteraction?.size ?? [6, 6, 6] };
    }

    if (asset.objectType === 'videoScreen') {
        obj.video = { ...(asset.video ?? { url: '' }) };
        obj.transform.rotation = [90, 0, 0];
    }

    return obj;
}
